import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchManagerCatalog,
  fetchManagerSyncStatus,
  isResolvableMapping,
  runManagerSync,
  setBiManagerKey,
  type ManagerSyncStatus,
} from "@/api/managerSync";

const statusKey = (userId: string | null) => ["managerSync", "status", userId] as const;

/**
 * D-048 status polling (MP3): 3s поки прогін queued/running АБО поки
 * resolvable-mapping ще не має жодного run-рядка (вікно між create і
 * хук-enqueue/runner-claim) — інакше полінг вимкнено.
 */
export function useManagerSyncStatus(userId: string | null, enabled = true) {
  return useQuery({
    queryKey: statusKey(userId),
    queryFn: ({ signal }) => fetchManagerSyncStatus(userId as string, signal),
    enabled: enabled && userId !== null,
    staleTime: 3_000,
    refetchInterval: (query) => {
      const d = query.state.data as ManagerSyncStatus | undefined;
      if (!d || !d.flagEnabled) return false;
      const s = d.lastRun?.status;
      if (s === "queued" || s === "running") return 3_000;
      // resolvable без run-рядка → авто-enqueue ось-ось зʼявиться (поллимо)
      if (d.lastRun === null && isResolvableMapping(d.mapping.status)) return 3_000;
      return false;
    },
  });
}

export function useManagerSyncRun(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => runManagerSync(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: statusKey(userId) }),
  });
}

export function useSetBiManagerKey(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string | null) => setBiManagerKey(userId, key),
    onSuccess: () => qc.invalidateQueries({ queryKey: statusKey(userId) }),
  });
}

/**
 * D-048 Phase 2 — BI-каталог менеджерів для picker'а. Лениво (enabled лише
 * коли picker відкрито); кеш = runner-maintained файл, тож 5 хв staleTime
 * не ризикує свіжістю.
 */
export function useManagerCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ["managerSync", "catalog"] as const,
    queryFn: ({ signal }) => fetchManagerCatalog(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}
